import { useEffect, useState } from 'react';
import type { Provider, Slot } from '@ampshift/shared';
import type { ApiClient } from '../api';
import { useI18n, formatDateTime } from '../i18n';
import { ConfirmationCardView, SlotPickerView, cardFromPendingAction, type ConfirmationCard } from './Surfaces';

interface Props { api: ApiClient; onBooked: () => void }

const localIsoDate = (d: Date): string => d.toISOString().slice(0, 10);

/** "Book an appointment": listProviders → findAvailableSlots → createAppointmentDraft → confirmation card → /v1/chat/confirm. */
export function BookingCard({ api, onBooked }: Props) {
  const { t, lang } = useI18n();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerId, setProviderId] = useState('');
  const [typeCode, setTypeCode] = useState<'FOLLOWUP' | 'NEW'>('FOLLOWUP');
  const [date, setDate] = useState(localIsoDate(new Date()));
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [card, setCard] = useState<ConfirmationCard | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api.tool('listProviders', {}).then((out) => setProviders(out.providers.filter((p) => p.active))).catch((e: unknown) => setError(e instanceof Error ? e.message : t('error')));
  }, [api, t]);

  const findSlots = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    setCard(null);
    try {
      const out = await api.tool('findAvailableSlots', { providerId: providerId || undefined, appointmentTypeCode: typeCode, from: date, limit: 12 });
      const names = new Map(providers.map((p) => [p.providerId, p.displayName]));
      setSlots(out.slots.map((s) => ({ ...s, providerName: s.providerName ?? names.get(s.providerId) })));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  const pick = async (slot: Slot) => {
    setBusy(true);
    setError(null);
    try {
      const out = await api.tool('createAppointmentDraft', { providerId: slot.providerId, start: slot.start, appointmentTypeCode: slot.appointmentTypeCode });
      setCard(cardFromPendingAction(out.pendingAction, t('bookingTitle'), [
        { label: t('provider'), value: slot.providerName ?? out.appointment.providerName ?? slot.providerId },
        { label: t('date'), value: formatDateTime(slot.start, lang) },
        { label: t('appointmentType'), value: slot.appointmentTypeCode === 'NEW' ? t('typeNew') : t('typeFollowUp') },
      ]));
      setSlots(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (c: ConfirmationCard, decision: 'confirm' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      await api.confirm({ pendingActionId: c.pendingActionId, decision, acknowledgedFindingIds: [] });
      setCard(null);
      if (decision === 'confirm') { setNotice(t('booked')); onBooked(); }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="book-title">
      <h2 id="book-title">{t('bookTitle')}</h2>
      <div className="row">
        <label className="field">{t('provider')}
          <select value={providerId} onChange={(e) => setProviderId(e.target.value)}>
            <option value="">{t('anyProvider')}</option>
            {providers.map((p) => <option key={p.providerId} value={p.providerId}>{lang === 'ar' && p.displayNameAr ? p.displayNameAr : p.displayName}</option>)}
          </select>
        </label>
        <label className="field">{t('appointmentType')}
          <select value={typeCode} onChange={(e) => setTypeCode(e.target.value === 'NEW' ? 'NEW' : 'FOLLOWUP')}>
            <option value="FOLLOWUP">{t('typeFollowUp')}</option>
            <option value="NEW">{t('typeNew')}</option>
          </select>
        </label>
        <label className="field">{t('date')}
          <input type="date" value={date} min={localIsoDate(new Date())} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={() => void findSlots()} disabled={busy}>{busy ? t('searching') : t('findSlots')}</button>
      </div>
      {error && <div className="err" role="alert" style={{ marginTop: 8 }}>{error}</div>}
      {notice && <div className="ok" role="status" style={{ marginTop: 8 }}>{notice}</div>}
      {slots && (slots.length === 0 ? <div className="muted" style={{ marginTop: 8 }}>{t('noSlots')}</div> : <SlotPickerView s={{ type: 'slot-picker', title: t('pickSlot'), slots }} onPick={(s) => void pick(s)} />)}
      {card && <ConfirmationCardView s={card} onConfirm={(c) => decide(c, 'confirm')} onReject={(c) => decide(c, 'reject')} />}
    </section>
  );
}
